import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiCheck, FiChevronDown, FiExternalLink, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: siteConfig.business.region,
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
};

export const makeOrderNumber = () => {
  const randomPart = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID().replaceAll("-", "").slice(0, 12).toUpperCase()
    : `${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36)}`.toUpperCase();
  return `${siteConfig.commerce.orderPrefix}-${Date.now().toString(36).toUpperCase()}-${randomPart}`;
};

export const buildPaymentUrl = ({ origin, orderNo, total, email, title }) => {
  const query = new URLSearchParams({
    orderNo,
    amount: Number(total).toFixed(2),
    currency: siteConfig.business.currency,
    email: email.trim(),
    success_url: `${origin}/payment?state=success`,
    cancel_url: `${origin}/payment?state=cancel`,
    title,
  });

  return `${origin}/create_payment.php?${query.toString()}`;
};

function CheckOut() {
  const { cartItems, appliedDiscountCode } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const discountApplied = appliedDiscountCode === siteConfig.commerce.discountCode;

  const detailedItems = useMemo(
    () => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product),
    [cartItems]
  );
  const subtotal = detailedItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const discount = discountApplied ? subtotal * siteConfig.commerce.discountRate : 0;
  const goodsTotal = subtotal - discount;
  const shipping = siteConfig.commerce.standardShipping;
  const total = goodsTotal + shipping;

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleInput = (event) => setField(event.target.name, event.target.value);

  const validate = () => {
    const nextErrors = {};
    ["email", "country", "firstName", "lastName", "address", "city", "region", "postalCode"].forEach((fieldName) => {
      if (!form[fieldName].trim()) nextErrors[fieldName] = "This field is required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Enter a valid email address";
    if (!siteConfig.checkout.countries.includes(form.country)) nextErrors.country = "Choose an available destination";
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      document.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`)?.focus();
      return;
    }

    const orderNo = makeOrderNumber();
    const origin = window.location.origin;
    const pendingOrder = {
      orderNo,
      email: form.email.trim(),
      amount: Number(total.toFixed(2)),
      currency: siteConfig.business.currency,
      title: detailedItems[0].product.name,
      createdAt: new Date().toISOString(),
    };
    window.sessionStorage.setItem(siteConfig.storage.pendingOrder, JSON.stringify(pendingOrder));

    setSubmitting(true);
    window.location.assign(buildPaymentUrl({
      origin,
      orderNo,
      total,
      email: form.email,
      title: detailedItems[0].product.name,
    }));
  };

  const field = (name, label, options = {}) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span>{label}{options.required && " *"}</span>
      <input name={name} type={options.type || "text"} autoComplete={options.autoComplete} value={form[name]} onChange={handleInput} placeholder={options.placeholder || ""} required={options.required} aria-required={options.required || undefined} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined} />
      {errors[name] && <small id={`${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  const orderSummary = (
    <div className="checkout-summary__content">
      <div className="checkout-products">
        {detailedItems.map((item) => <article key={`${item.id}-${item.size}-${item.color}`}><div><img src={item.product.image} alt={`${item.product.name} — ${item.product.colorName}`} /><span>{item.quantity}</span></div><p><strong>{item.product.name}</strong><small>{item.product.colorName} · {item.size}</small></p><b>{formatPrice(item.product.price * item.quantity)}</b></article>)}
      </div>
      <div className="checkout-summary__rows"><p><span>Product subtotal</span><b>{formatPrice(subtotal)}</b></p>{discountApplied && <p className="checkout-summary__discount"><span>{appliedDiscountCode}</span><b>−{formatPrice(discount)}</b></p>}<p><span>Fixed shipping</span><b>{formatPrice(shipping)}</b></p></div>
      <div className="checkout-summary__total"><span>Total <small>{siteConfig.business.currency}</small></span><b>{formatPrice(total)}</b></div>
    </div>
  );

  if (detailedItems.length === 0) {
    return <main className="checkout-empty"><span>{siteConfig.logo.symbol}</span><h1>Your checkout is empty.</h1><p>Add a garment to your bag before continuing.</p><Link className="button-primary" to="/shop">Return to shop</Link></main>;
  }

  return (
    <main className="checkout-page">
      <div className="checkout-mobile-summary"><details><summary><span>Order summary <FiChevronDown /></span><strong>{formatPrice(total)}</strong></summary>{orderSummary}</details></div>
      <div className="checkout-layout">
        <section className="checkout-form-wrap">
          <div className="checkout-intro"><p className="eyebrow">Secure checkout</p><h1>Delivery first.<br />Payment next.</h1><p>Enter only the contact and address information needed for this order. Card details are entered on Stripe after Pay Now.</p></div>
          <form className="checkout-form" onSubmit={handleSubmit} noValidate>
            <section>
              <div className="checkout-section-title"><h2>Contact</h2><span>1 / 3</span></div>
              {field("email", "Email address", { required: true, type: "email", autoComplete: "email", placeholder: "you@example.com" })}
            </section>

            <section>
              <div className="checkout-section-title"><h2>Delivery address</h2><span>2 / 3</span></div>
              <label className={`checkout-field ${errors.country ? "has-error" : ""}`}><span>Country / region *</span><select name="country" value={form.country} onChange={handleInput} required aria-required="true" aria-invalid={Boolean(errors.country)}>{siteConfig.checkout.countries.map((country) => <option key={country}>{country}</option>)}</select>{errors.country && <small role="alert">{errors.country}</small>}</label>
              <div className="checkout-two-fields">{field("firstName", "First name", { required: true, autoComplete: "given-name" })}{field("lastName", "Last name", { required: true, autoComplete: "family-name" })}</div>
              {field("address", "Street address", { required: true, autoComplete: "address-line1" })}
              {field("apartment", "Apartment, suite, etc. (optional)", { autoComplete: "address-line2" })}
              <div className="checkout-three-fields">{field("city", "City", { required: true, autoComplete: "address-level2" })}{field("region", "Province / region", { required: true, autoComplete: "address-level1" })}{field("postalCode", "Postal code", { required: true, autoComplete: "postal-code" })}</div>
              <div className="checkout-shipping-method"><span>Shipping method</span><label><input type="radio" checked readOnly name="shipping" /><span><strong>Standard delivery</strong><small>Order-specific updates by email</small></span><b>{formatPrice(shipping)}</b></label></div>
            </section>

            <section>
              <div className="checkout-section-title"><h2>Payment hand-off</h2><span>3 / 3</span></div>
              <div className="checkout-payment checkout-payment--redirect">
                <FiLock />
                <div><strong>Stripe hosted checkout</strong><p>Pay Now redirects to Stripe. This site does not collect or store your card number, expiry date or security code.</p></div>
                <FiExternalLink />
              </div>
            </section>

            <button className="checkout-submit button-primary" type="submit" disabled={submitting}>{submitting ? <><span className="checkout-spinner" /> Redirecting to Stripe…</> : <><FiLock /> Pay now · {formatPrice(total)}</>}</button>
            <p className="checkout-terms">By continuing, you agree to the <Link to="/terms">Terms</Link>, <Link to="/privacy">Privacy Policy</Link> and <Link to="/payments">Payment Policy</Link>.</p>
          </form>
        </section>

        <aside className="checkout-summary"><div className="checkout-summary__sticky"><p className="eyebrow">Your order</p><h2>{detailedItems.reduce((count, item) => count + item.quantity, 0)} pieces</h2>{orderSummary}<div className="checkout-summary__assurance"><FiCheck /><p><strong>Return rules shown before payment</strong><span>Prompt contact, condition rules and mandatory rights apply.</span></p></div></div></aside>
      </div>
    </main>
  );
}

export default CheckOut;
